import type { ViewComponent } from "@frui.ts/views";
import type ChargeDetailInfoViewModel from "viewModels/callCenter/search/charges/chargeDetail/chargeDetailInfoViewModel";
import { observer } from "mobx-react-lite";
import ChargeDetailSection from "./chargeDetailSection";
import { Col, Form, Row } from "react-bootstrap";
import FakeInput from "@edaz/edazkit/controls/FakeInput";
import { formatDate, formatDateTime } from "utils/localizations";
import React from "react";
import { vehicleRegisterVehicleStatusToVariant } from "entities/vehicleRegisterVehicleStatus";
import { FormLabelWithTooltip } from "controls/formLabelWithTooltip";
import { emptyValue } from "utils/emptyValue";

// eslint-disable-next-line sonarjs/cognitive-complexity
const ChargeDetailVehicleSection: ViewComponent<ChargeDetailInfoViewModel> = observer(({ vm }) => {
  const vehicleDetail = vm.item.vehicleRegisterVehicleDetail;
  if (!vehicleDetail) {
    return null;
  }
  return (
    <ChargeDetailSection title={vm.translate("vehicleDetail")}>
      <Row>
        <Form.Group as={Col} md={6} xl={4}>
          <FormLabelWithTooltip tooltipText={vm.translate("externalVehicleIdentTooltip")}>
            {vm.translateSubAttribute("vehicleRegisterVehicleDetailResponse", "externalVehicleIdent")}
          </FormLabelWithTooltip>
          <FakeInput target={vehicleDetail} property="externalVehicleIdent" valueFormatter={value => value ?? emptyValue} />
        </Form.Group>
        <Form.Group as={Col} md={6} xl={8}>
          <Form.Label>{vm.translateSubAttribute("vehicleRegisterVehicleDetailResponse", "vin")}</Form.Label>
          <FakeInput target={vehicleDetail} property="vin" valueFormatter={value => value ?? emptyValue} />
        </Form.Group>
        <Form.Group as={Col} md={6} xl={4}>
          <Form.Label>{vm.translateSubAttribute("vehicleRegisterVehicleDetailResponse", "licensePlate")}</Form.Label>
          <FakeInput target={vehicleDetail} property="licensePlate" valueFormatter={value => value ?? emptyValue} />
        </Form.Group>
        <Form.Group as={Col} md={6} xl={4}>
          <Form.Label>
            {vm.translateSubAttribute("vehicleRegisterVehicleDetailResponse", "licensePlateValidSinceDate")}
          </Form.Label>
          <FakeInput
            target={vehicleDetail}
            property="licensePlateValidSinceDate"
            valueFormatter={value => (value ? formatDate(value) : emptyValue)}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} xl={4}>
          <Form.Label>
            {vm.translateSubAttribute("vehicleRegisterVehicleDetailResponse", "licensePlateValidUntilDate")}
          </Form.Label>
          <FakeInput
            target={vehicleDetail}
            property="licensePlateValidUntilDate"
            valueFormatter={value => (value ? formatDate(value) : emptyValue)}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} xl={4}>
          <FormLabelWithTooltip tooltipText={vm.translate("vehicleStatusTooltip")}>
            {vm.translateSubAttribute("vehicleRegisterVehicleDetailResponse", "vehicleStatus")}
          </FormLabelWithTooltip>
          <FakeInput
            target={vehicleDetail}
            property="vehicleStatus"
            valueFormatter={value => {
              const formattedValue = vm.translateVehicleStatus(value);
              return <strong className={`text-${vehicleRegisterVehicleStatusToVariant(value)}`}>{formattedValue}</strong>;
            }}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} xl={4}>
          <Form.Label>
            {vm.translateSubAttribute("vehicleRegisterVehicleDetailResponse", "vehicleStatusValidSinceDate")}
          </Form.Label>
          <FakeInput
            target={vehicleDetail}
            property="vehicleStatusValidSinceDate"
            valueFormatter={value => (value ? formatDate(value) : emptyValue)}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} xl={4}>
          <Form.Label>
            {vm.translateSubAttribute("vehicleRegisterVehicleDetailResponse", "vehicleStatusValidUntilDate")}
          </Form.Label>
          <FakeInput
            target={vehicleDetail}
            property="vehicleStatusValidUntilDate"
            valueFormatter={value => (value ? formatDate(value) : emptyValue)}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} xl={3}>
          <Form.Label>{vm.translateSubAttribute("vehicleRegisterVehicleDetailResponse", "category")}</Form.Label>
          <FakeInput target={vehicleDetail} property="category" valueFormatter={value => value ?? emptyValue} />
        </Form.Group>
        <Form.Group as={Col} md={6} xl={3}>
          <Form.Label>{vm.translateSubAttribute("vehicleRegisterVehicleDetailResponse", "categoryName")}</Form.Label>
          <FakeInput target={vehicleDetail} property="categoryName" valueFormatter={value => value ?? emptyValue} />
        </Form.Group>
        <Form.Group as={Col} md={6} xl={6}>
          <Form.Label>{vm.translateSubAttribute("vehicleRegisterVehicleDetailResponse", "maximumAllowedWeight")}</Form.Label>
          <FakeInput
            target={vehicleDetail}
            property="maximumAllowedWeight"
            valueFormatter={value => vm.formatHeight(value) ?? emptyValue}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} xl={3}>
          <Form.Label>{vm.translateSubAttribute("vehicleRegisterVehicleDetailResponse", "emissionsCo2")}</Form.Label>
          <FakeInput target={vehicleDetail} property="emissionsCo2" valueFormatter={value => value ?? emptyValue} />
        </Form.Group>
        <Form.Group as={Col} md={6} xl={9}>
          <Form.Label>{vm.translateSubAttribute("vehicleRegisterVehicleDetailResponse", "fuelTypes")}</Form.Label>
          <FakeInput
            target={vehicleDetail}
            property="fuelTypes"
            valueFormatter={value => (value?.length > 0 ? vm.translateVehicleRegisterVehicleFuelType(value) : emptyValue)}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} xl={4}>
          <Form.Label>{vm.translateSubAttribute("vehicleRegisterVehicleDetailResponse", "fuelTypeName1")}</Form.Label>
          <FakeInput target={vehicleDetail} property="fuelTypeName1" valueFormatter={value => value ?? emptyValue} />
        </Form.Group>
        <Form.Group as={Col} md={6} xl={4}>
          <Form.Label>{vm.translateSubAttribute("vehicleRegisterVehicleDetailResponse", "fuelTypeName2")}</Form.Label>
          <FakeInput target={vehicleDetail} property="fuelTypeName2" valueFormatter={value => value ?? emptyValue} />
        </Form.Group>
        <Form.Group as={Col} md={6} xl={4}>
          <Form.Label>{vm.translateSubAttribute("vehicleRegisterVehicleDetailResponse", "fuelTypeName3")}</Form.Label>
          <FakeInput target={vehicleDetail} property="fuelTypeName3" valueFormatter={value => value ?? emptyValue} />
        </Form.Group>
        <Form.Group as={Col} md={6} xl={6}>
          <Form.Label>{vm.translateSubAttribute("vehicleRegisterVehicleDetailResponse", "classifiedFuelType")}</Form.Label>
          <FakeInput
            target={vehicleDetail}
            property="classifiedFuelType"
            valueFormatter={value => (value ? vm.translateVehicleRegisterVehicleClassifiedFuelType(value) : emptyValue)}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} xl={6}>
          <Form.Label>{vm.translateSubAttribute("vehicleRegisterVehicleDetailResponse", "updatedAt")}</Form.Label>
          <FakeInput
            target={vehicleDetail}
            property="updatedAt"
            valueFormatter={value => (value ? formatDateTime(value) : emptyValue)}
          />
        </Form.Group>
      </Row>
    </ChargeDetailSection>
  );
});

export default ChargeDetailVehicleSection;
